import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ButtonOne from "../../common/normal-button";
import { ColorsConstants } from "../../cssUtils/constants";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AppDropdown from "../../common/app-dropdown";
import AppTable from "../../common/app-table";
import { loadFilters, saveFilters, FILTER_NAMESPACES } from "../../utils/filterStorage";

const dummyLogs = [
  {
    id: 1,
    dateTime: "Jan 30, 2026, 7:26:54 PM",
    actionedBy: "ADMIN",
    action: "Logged In",
    description: "User Logged In",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "157.50.129.123",
  },
  {
    id: 2,
    dateTime: "Jan 30, 2026, 7:25:50 PM",
    actionedBy: "ADMIN",
    action: "Logged out",
    description: "User Logged out",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "157.50.132.17",
  },
  {
    id: 3,
    dateTime: "Jan 30, 2026, 7:16:39 PM",
    actionedBy: "ADMIN",
    action: "Logged In",
    description: "User Logged In",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "157.50.132.17",
  },
  {
    id: 4,
    dateTime: "Jan 30, 2026, 7:16:14 PM",
    actionedBy: "ADMIN",
    action: "Login Failed",
    description: "User Login Failed",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "157.50.132.17",
  },
  {
    id: 5,
    dateTime: "Jan 30, 2026, 7:14:11 PM",
    actionedBy: "ADMIN",
    action: "Logged out",
    description: "User Logged out",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "157.50.132.17",
  },
  {
    id: 6,
    dateTime: "Jan 30, 2026, 6:40:17 PM",
    actionedBy: "ADMIN",
    action: "Logged In",
    description: "User Logged In",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "157.50.132.17",
  },
  {
    id: 7,
    dateTime: "Jan 30, 2026, 6:00:59 PM",
    actionedBy: "ADMIN",
    action: "Logged In",
    description: "User Logged In",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "223.181.63.97",
  },
  {
    id: 8,
    dateTime: "Jan 30, 2026, 5:51:52 PM",
    actionedBy: "ADMIN",
    action: "Logged out",
    description: "User Logged out",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "223.181.63.97",
  },
  {
    id: 9,
    dateTime: "Jan 30, 2026, 3:55:18 PM",
    actionedBy: "ADMIN",
    action: "Logged In",
    description: "User Logged In",
    resource: "User",
    resourceName: "ADMIN",
    ipAddress: "223.181.63.97",
  },
];

const selectSx = {
  height: 40,
  borderRadius: "8px",
  backgroundColor: "#fff",
  fontSize: 13,
  color: "#04142F",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#DFE5EC",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#DFE5EC",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ColorsConstants.primaryBackgroundColor,
  },
  "& .MuiSelect-icon": {
    color: "#6C7C93",
    fontSize: 20,
  },
};

// ✅ KEY FIX: No custom InputProps/endAdornment override here.
// Providing a custom endAdornment in InputProps replaces MUI's internal
// picker open button, which breaks the click-to-open behaviour.
// Let MUI render its own calendar icon button so the modal opens correctly.

const tableColumns = [
  { id: "dateTime",     label: "Date & Time"   },
  { id: "actionedBy",  label: "Actioned By"   },
  { id: "action",      label: "Action"        },
  { id: "description", label: "Description"   },
  { id: "resource",    label: "Resource"      },
  { id: "resourceName",label: "Resource Name" },
  { id: "ipAddress",   label: "IP Address"    },
];

const datePickerSlotProps = {
  textField: {
    fullWidth: true,
    size: "small",
    sx: {
      backgroundColor: "#fff",
      borderRadius: "8px",
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        height: 40,
        fontSize: 13,
        color: "#04142F",
        "& fieldset": { borderColor: "#DFE5EC" },
        "&:hover fieldset": { borderColor: "#DFE5EC" },
        "&.Mui-focused fieldset": {
          borderColor: ColorsConstants.primaryBackgroundColor,
        },
      },
      // Style MUI's built-in calendar icon button to match the theme
      "& .MuiInputAdornment-root .MuiIconButton-root": {
        color: "#6C7C93",
        padding: "4px",
        "&:hover": {
          color: ColorsConstants.primaryBackgroundColor,
          backgroundColor: "transparent",
        },
      },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        fontSize: 16,
      },
    },
  },
};

const labelSx = {
  fontSize: 13,
  fontWeight: 500,
  color: "#04142F",
  mb: 0.75,
};

const tableHeadCellSx = {
  backgroundColor: ColorsConstants.primaryBackgroundColor,
  color: "#fff",
  fontSize: 13,
  fontWeight: 500,
  py: 1.5,
  px: 2,
  whiteSpace: "nowrap",
  borderBottom: "none",
};

const tableBodyCellSx = {
  fontSize: 13,
  color: "#04142F",
  py: "13px",
  px: 2,
  whiteSpace: "nowrap",
  borderBottom: "1px solid #F0F4F8",
};

const AuditLogs = (props) => {
  const [savedFilters] = useState(() => {
    const s = loadFilters(FILTER_NAMESPACES.AUDIT_TRAIL, {
      user: '', resource: '', resourceName: '', action: '',
      startDate: null, endDate: null,
    });
    return {
      ...s,
     startDate:
     s.startDate && dayjs(s.startDate).isValid()
       ? dayjs(s.startDate)
       : null,
    endDate:
    s.endDate && dayjs(s.endDate).isValid()
      ? dayjs(s.endDate)
      : null,
    };
  });
  const [user, setUser] = React.useState(savedFilters.user);
  const [resource, setResource] = React.useState(savedFilters.resource);
  const [resourceName, setResourceName] = React.useState(savedFilters.resourceName);
  const [action, setAction] = React.useState(savedFilters.action);
  const [startDate, setStartDate] = React.useState(savedFilters.startDate);
  const [endDate, setEndDate] = React.useState(savedFilters.endDate);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });
  const [rowCount, setRowCount] = useState(0); // set this from your API response

  useEffect(() => {
    saveFilters(FILTER_NAMESPACES.AUDIT_TRAIL, {
      user, resource, resourceName, action,
      startDate: startDate?.isValid?.() ? startDate.toISOString() : null,
      endDate: endDate?.isValid?.() ? endDate.toISOString() : null,
    });
  }, [user, resource, resourceName, action, startDate, endDate]);

  const handlePageChange = (event, newPage) => {
    setController((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };
  const handleReset = () => {
    setUser("");
    setResource("");
    setResourceName("");
    setAction("");
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Box sx={{ backgroundColor: "#FAFAFA", minHeight: "100vh" }}>

        {/* Filter Header */}
        <Box
          sx={{
  
            px: 3,
            py: 2.5,
          }}
        >
          {/* Row 1: Dropdowns */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              
              <AppDropdown
                endIcon={<ExpandMoreIcon />}
                menuItems={[
                  { label: "ADMIN", value: "ADMIN" },
                  { label: "USER1", value: "USER1" },
                ]}
                selectedValue={user}
                onMenuSelect={(value) => setUser(value)}
                sx={{ width: "100%" }}
              >
                {user || <span style={{ color: "#6C7C93" }}>Select User</span>}
              </AppDropdown>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppDropdown
                endIcon={<ExpandMoreIcon />}
                menuItems={[
                  { label: "User", value: "User" },
                  { label: "System", value: "System" },
                ]}
                selectedValue={resource}
                onMenuSelect={(value) => setResource(value)}
                sx={{ width: "100%" }}
              >
                {resource || <span style={{ color: "#6C7C93" }}>Select Resource</span>}
              </AppDropdown>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppDropdown
                endIcon={<ExpandMoreIcon />}
                menuItems={[
                  { label: "ADMIN", value: "ADMIN" },
                ]}
                selectedValue={resourceName}
                onMenuSelect={(value) => setResourceName(value)}
                sx={{ width: "100%" }}
              >
                {resourceName || <span style={{ color: "#6C7C93" }}>Select Resource Name</span>}
              </AppDropdown>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppDropdown
                endIcon={<ExpandMoreIcon />}
                menuItems={[
                  { label: "Logged In", value: "Logged In" },
                  { label: "Logged out", value: "Logged out" },
                  { label: "Login Failed", value: "Login Failed" },
                ]}
                selectedValue={action}
                onMenuSelect={(value) => setAction(value)}
                sx={{ width: "100%" }}
              >
                {action || <span style={{ color: "#6C7C93" }}>Select Actions</span>}
              </AppDropdown>
            </Grid>
          </Grid>

          {/* Row 2: Date pickers + buttons */}
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={labelSx}>Start Date</Typography>
              <DateTimePicker
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={datePickerSlotProps}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={labelSx}>End Date</Typography>
              <DateTimePicker
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={datePickerSlotProps}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", width: "100%" }}>
                <ButtonOne
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                  sx={{ flex: 1 }}
                >
                  Reset
                </ButtonOne>

                <ButtonOne
                  variant="contained"
                  sx={{
                    flex: 1,
                    backgroundColor: ColorsConstants.primaryPurple100,
                    "&:hover": {
                      backgroundColor: ColorsConstants.primaryBackgroundColor,
                      opacity: 0.9,
                    },
                  }}
                >
                  Download
                </ButtonOne>

                <ButtonOne
                  variant="contained"
                  sx={{ flex: 1 }}
                >
                  Submit
                </ButtonOne>
              </Box>
            </Grid>
          </Grid>
        </Box>

       {/* Table Section */}
        <Box sx={{ p: 3 }}>
          <Paper 
          
            sx={{ 
              width: "100%", 
              overflow: "hidden", 
              border: "none", 
              boxShadow: "none", 
              borderRadius: "8px",
              mb: 2,

            }}
          >
          <AppTable
            columns={tableColumns}
            rows={dummyLogs}
            rowKey="id"
            emptyMessage="No logs found"
            maxHeight={600}
            containerSx={{
              minWidth: 900,
              scrollbarWidth: "thin",
              scrollbarColor: "#C5CEDB #F3F6FB",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
              "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
            }}
            count={rowCount}
            page={controller.page}
            rowsPerPage={controller.rowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AuditLogs;